import { z } from 'zod';
import { bcp47, jsonObject, timestamp, uuid } from './common.js';
import { difficultyLevel } from './document.js';
import { listQueryInput, paginated } from './list.js';
/**
 * Async engine (§4, E11). `jobs` (SKIP-LOCKED queue), `job_logs` (durable
 * user-visible trail streamed over `job:{jobId}`), `job_checkpoints` (one JSON
 * state row per stage — resumability).
 */
/**
 * The 14 checkpointed worker types. Names use underscores to match the
 * `action_credit_costs.action_type` vocabulary (§21). `WORKER_ROLE` selects a
 * subset per pool (heavy vs light split, §6).
 */
export const jobType = z.enum([
    'outline',
    'draft',
    'review',
    'illustrate',
    'render',
    'expand',
    'cut_continue',
    'difficulty_elevate',
    'translate',
    'moderate',
    'auto_categorize',
    'ingest',
    'search_index',
    'notify',
    // Danger Zone "Export my data" (F10 self-service, WS-ACCOUNT).
    'account_export',
]);
export const jobStatus = z.enum(['queued', 'running', 'succeeded', 'failed', 'canceled']);
export const jobSchema = z.object({
    id: uuid,
    type: jobType,
    status: jobStatus,
    /** Owner of the work — for `user:` notifications + credit attribution. */
    userId: uuid,
    payload: jsonObject, // validated per-type by jobPayloadSchemas
    currentCheckpoint: z.string().nullable(),
    attempts: z.number().int().nonnegative(),
    maxAttempts: z.number().int().positive(),
    availableAt: timestamp, // backoff/scheduling gate
    lockedBy: z.string().nullable(), // worker id holding the lease
    lockedAt: timestamp.nullable(), // lease start; reaper requeues if stale
    lastError: z.string().nullable(), // safe reason only — no secrets/prompts
    createdAt: timestamp,
    updatedAt: timestamp,
});
export const jobLogLevel = z.enum(['debug', 'info', 'warn', 'error']);
export const jobLogSchema = z.object({
    id: uuid,
    jobId: uuid,
    level: jobLogLevel,
    stage: z.string().nullable(),
    message: z.string(), // human-readable, surfaced to the owner (E12)
    dataJsonb: jsonObject.nullable(), // structured: token counts, model id, batch size
    createdAt: timestamp,
});
export const jobCheckpointSchema = z.object({
    id: uuid,
    jobId: uuid,
    stage: z.string().min(1),
    stateJsonb: jsonObject, // bookforge workdir for this stage
    artifactFileId: uuid.nullable(),
    createdAt: timestamp,
});
/** Live push shape on `job:{jobId}` (§5.5). */
export const jobProgressPush = z.object({
    jobId: uuid,
    status: jobStatus,
    currentCheckpoint: z.string().nullable(),
    event: z.enum(['status', 'checkpoint', 'log', 'complete']),
});
// --- reattach DTOs (job.subscribe / job.get / job.logs, E20 §2) -------------
export const jobSubscribeInput = z.object({ jobId: uuid });
export const jobGetInput = z.object({ jobId: uuid });
/**
 * Reattach snapshot (E20 §2: "re-subscribe every tracked `job:{jobId}`, fetch
 * latest `job_checkpoints` stage + `jobs.status` + recent `job_logs` once,
 * then live via pushes"). `job.subscribe` additionally subscribes the caller
 * to `job:{jobId}` before returning this; `job.get` returns the same shape
 * without subscribing (e.g. a one-off poll from a non-live surface).
 */
export const jobSnapshot = z.object({
    job: jobSchema,
    checkpoints: z.array(jobCheckpointSchema),
    /** A recent tail, not the full history — page further back via `job.logs`. */
    recentLogs: z.array(jobLogSchema),
});
export const jobLogSortColumns = ['createdAt'];
export const jobLogsQuery = listQueryInput(jobLogSortColumns, {
    defaultSort: 'createdAt',
    defaultDir: 'asc',
    defaultPageSize: 50,
}).extend({ jobId: uuid });
export const jobLogsResult = paginated(jobLogSchema);
// --- per-worker payload schemas ----------------------------------------------
// Job payloads are the worker inputs. Shapes below are the F0a-defined contract
// (the specs pin the DAG + DTOs, not every field), designed to align with the
// document-version DAG; workers (F0g/WS-*) parse `jobPayloadSchemas[job.type]`.
export const outlineJobPayload = z.object({ documentId: uuid });
export const draftJobPayload = z.object({ documentId: uuid, outlineId: uuid });
export const reviewJobPayload = z.object({ documentId: uuid, versionId: uuid });
export const illustrateJobPayload = z.object({
    documentId: uuid,
    versionId: uuid,
    /** Restrict to specific chapters; empty/omitted = whole document. */
    chapterIds: z.array(z.string().min(1)).optional(),
    /** Flux seed lock for reproducible re-runs. */
    seed: z.number().int().optional(),
});
/** Deliverable formats the `render` worker produces (F1 core render + F67 export). */
export const renderFormat = z.enum(['pdf', 'docx', 'md', 'txt']);
export const renderJobPayload = z.object({
    documentId: uuid,
    versionId: uuid,
    format: renderFormat,
    /** F65 document Theme preset name (re-render only, no text regen). */
    theme: z.string().optional(),
});
export const expandJobPayload = z.object({
    documentId: uuid,
    versionId: uuid,
    instructions: z.string().optional(),
});
export const cutContinueJobPayload = z.object({
    documentId: uuid,
    versionId: uuid,
    fromChapterId: z.string().min(1),
    instructions: z.string().optional(),
});
export const difficultyElevateJobPayload = z.object({
    documentId: uuid,
    versionId: uuid,
    targetLevel: difficultyLevel,
});
export const translateJobPayload = z.object({
    documentId: uuid,
    versionId: uuid,
    targetLanguages: z.array(bcp47).min(1),
});
/** Two gates through one classifier (§6.8): `generate` (pre-paid-stage) or `share`. */
export const moderateJobPayload = z.object({
    gate: z.enum(['generate', 'share']),
    documentId: uuid,
    versionId: uuid.nullable(),
    shareId: uuid.nullable(),
});
export const autoCategorizeJobPayload = z.object({ documentId: uuid });
export const ingestJobPayload = z.object({ uploadId: uuid });
export const searchIndexJobPayload = z.object({ shareId: uuid });
export const notifyJobPayload = z.object({ notificationId: uuid });
/**
 * Danger Zone "Export my data" (F10 self-service). `userId` is carried in
 * the payload (not inferred from `jobs.userId` alone) because `PipelineJob`
 * (apps/workers/src/pipeline.ts) only threads `job.payload` into a stage's
 * `StageContext` — mirrors every other job type's own self-contained-payload
 * convention (e.g. `outlineJobPayload.documentId` duplicates info already on
 * the row) rather than reaching outside the payload for it.
 */
export const accountExportJobPayload = z.object({ userId: uuid });
/** Lookup table the worker harness uses: `jobPayloadSchemas[job.type].parse(...)`. */
export const jobPayloadSchemas = {
    outline: outlineJobPayload,
    draft: draftJobPayload,
    review: reviewJobPayload,
    illustrate: illustrateJobPayload,
    render: renderJobPayload,
    expand: expandJobPayload,
    cut_continue: cutContinueJobPayload,
    difficulty_elevate: difficultyElevateJobPayload,
    translate: translateJobPayload,
    moderate: moderateJobPayload,
    auto_categorize: autoCategorizeJobPayload,
    ingest: ingestJobPayload,
    search_index: searchIndexJobPayload,
    notify: notifyJobPayload,
    account_export: accountExportJobPayload,
};
